import {
  useCreateLayoutOptimizationResultMutation,
  useFileUpload,
  useLayoutId,
  useProjectId,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { ResultFileType, StatusVariant } from '@consigli/types';
import { useState, useMemo } from 'react';

import { Field, Select, TextInput, SheetForm } from '@/components';
import { FileUpload } from '@/components/file-upload';
import { errorToast, successToast } from '@/components/toast';
import { getFileTypeFromFilename } from '@/utils/file-type-detection';

const fileTypeOptions = Object.values(ResultFileType).map((type) => ({
  label: type,
  value: type,
}));

export const UploadResultForm = () => {
  const projectId = useProjectId();
  const layoutId = useLayoutId();
  const [files, setFiles] = useState<File[]>([]);
  const [customName, setCustomName] = useState('');
  const [selectedFileType, setSelectedFileType] = useState<ResultFileType | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ files?: string; fileType?: string }>({});

  const [createResult] = useCreateLayoutOptimizationResultMutation();

  // Build the full filename with extension
  const fullFilename = useMemo(() => {
    if (files.length !== 1) return '';
    if (!customName) return '';

    const extension = files[0].name.split('.').pop();
    return `${customName}.${extension}`;
  }, [files, customName]);

  const { upload } = useFileUpload(fullFilename);

  const validateForm = (): boolean => {
    const newErrors: { files?: string; fileType?: string } = {};

    if (files.length === 0) {
      newErrors.files = 'Please upload at least one file';
    }

    if (files.length === 1 && !selectedFileType) {
      newErrors.fileType = 'Please select a file type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (isSubmitting) {
      return false;
    }

    if (!validateForm()) {
      return false;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (files.length === 1) {
        const file = files[0];
        const uploadedFile = await upload(file, projectId);

        await createResult({
          projectId,
          layoutId,
          layoutResult: {
            fileId: uploadedFile.id,
            type: selectedFileType!,
          },
        }).unwrap();

        successToast({ message: 'Result uploaded successfully' });
      } else {
        let successCount = 0;
        for (const file of files) {
          try {
            const uploadedFile = await upload(file, projectId);
            const detectedFileType = getFileTypeFromFilename(file.name);

            await createResult({
              projectId,
              layoutId,
              layoutResult: {
                fileId: uploadedFile.id,
                type: detectedFileType,
              },
            }).unwrap();

            successCount++;
          } catch (error) {
            console.error(`Error uploading file ${file.name}:`, error);
          }
        }

        if (successCount === files.length) {
          successToast({ message: `${files.length} results uploaded successfully` });
        } else {
          successToast({
            message: `${successCount} of ${files.length} results uploaded successfully`,
          });
        }
      }

      setFiles([]);
      setCustomName('');
      setSelectedFileType(null);
      setIsOpen(false);
      return true;
    } catch (error) {
      console.error('Failed to upload result:', error);
      errorToast({ message: 'Failed to upload result' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setCustomName('');
    setSelectedFileType(null);
    setErrors({});
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      handleReset();
    }
  };

  // Clear file error when files change
  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles);
    if (newFiles.length > 0 && errors.files) {
      setErrors((prev) => ({ ...prev, files: undefined }));
    }
  };

  const handleFileTypeChange = (type: ResultFileType) => {
    setSelectedFileType(type);
    if (errors.fileType) {
      setErrors((prev) => ({ ...prev, fileType: undefined }));
    }
  };

  return (
    <SheetForm
      title="Upload Result"
      description="Upload result files for this layout optimization"
      trigger={<Button>Upload Result</Button>}
      onSubmit={handleSubmit}
      onReset={handleReset}
      submitLabel="Upload"
      resetLabel="Clear"
      showReset
      open={isOpen}
      onOpenChange={handleOpenChange}
      isSubmitting={isSubmitting}
    >
      <div className="space-y-4">
        <Field label="Files" htmlFor="files-input" required error={errors.files}>
          <FileUpload
            files={files}
            setFiles={handleFilesChange}
            alert={{
              title: 'Upload Result Files',
              description:
                'Upload result files such as reports, drawings, IFC models, or Revit files. File types will be auto-detected based on filenames.',
              variant: StatusVariant.INFO,
            }}
          />
        </Field>

        {files.length === 1 && (
          <>
            <Field label="Custom Filename" htmlFor="custom-name-input">
              <TextInput
                placeholder="Enter custom filename (optional)"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                {customName
                  ? `Final filename: ${fullFilename}`
                  : `Leave empty to use original filename: ${files[0]?.name}`}
              </p>
            </Field>

            <Field label="File Type" htmlFor="file-type-select" required error={errors.fileType}>
              <Select
                options={fileTypeOptions}
                placeholder="Select file type"
                value={fileTypeOptions.find((opt) => opt.value === selectedFileType)}
                onValueChange={(option) => handleFileTypeChange(option.value as ResultFileType)}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                className="w-full"
              />
            </Field>
          </>
        )}

        {files.length > 1 && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Multiple files:</strong> Files will keep their original names and file types
              will be auto-detected based on filenames. You can edit file types after upload if
              needed.
            </p>
          </div>
        )}
      </div>
    </SheetForm>
  );
};
